import { ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import type {
  SubmissionAttemptFieldItem,
  SubmissionAttemptFields,
} from '@/services/api/agentflowService';
import { usePatchSubmissionAttemptMutation } from '@/services/api/agentflowService';

// ---------------------------------------------------------------------------
// Blocking field — missing value, needs operator input
// ---------------------------------------------------------------------------

const BlockingFieldCard = ({
  field,
  matterId,
  requestId,
}: {
  field: SubmissionAttemptFieldItem;
  matterId: string;
  requestId: string;
}) => {
  const [patchAttempt, { isLoading }] = usePatchSubmissionAttemptMutation();
  const [value, setValue] = useState('');
  const [isDismissing, setIsDismissing] = useState(false);

  const handleSave = useCallback(async () => {
    if (!value.trim()) return;
    try {
      await patchAttempt({
        matterId,
        requestId,
        body: {
          action_type: 'set_field_value',
          payload: {
            field_name: field.field_name,
            value: value.trim(),
            ...(field.field_id ? { field_id: field.field_id } : {}),
          },
        },
      }).unwrap();
    } catch {
      toast.error(`Failed to save ${field.field_name}`);
    }
  }, [matterId, requestId, field, value, patchAttempt]);

  const handleDismiss = useCallback(async () => {
    setIsDismissing(true);
    try {
      await patchAttempt({
        matterId,
        requestId,
        body: {
          action_type: 'dismiss_field',
          payload: {
            field_name: field.field_name,
            ...(field.field_id ? { field_id: field.field_id } : {}),
          },
        },
      }).unwrap();
    } catch {
      toast.error(`Failed to dismiss ${field.field_name}`);
    } finally {
      setIsDismissing(false);
    }
  }, [matterId, requestId, field, patchAttempt]);

  return (
    <div className="rounded-lg border-l-[3px] border-l-amber-400 border border-zinc-200 px-4 py-3 space-y-2">
      <div className="flex items-center gap-1.5">
        <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500 shrink-0" />
        <span className="text-sm font-medium text-zinc-800">
          {field.field_name}
        </span>
      </div>
      <div className="flex gap-2">
        <Input
          placeholder={`Enter ${field.field_name.toLowerCase()}...`}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="flex-1"
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSave();
          }}
        />
        <Button
          size="sm"
          onClick={handleSave}
          disabled={isLoading || isDismissing || !value.trim()}
        >
          {isLoading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            'Save'
          )}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleDismiss}
          disabled={isLoading || isDismissing}
        >
          {isDismissing ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            'Dismiss'
          )}
        </Button>
      </div>
      {field.agent_note && (
        <p className="text-xs text-zinc-500 italic">{field.agent_note}</p>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Optional field — useful to include, but does not block submission
// ---------------------------------------------------------------------------

const OptionalFieldCard = ({
  field,
  matterId,
  requestId,
}: {
  field: SubmissionAttemptFieldItem;
  matterId: string;
  requestId: string;
}) => {
  const [patchAttempt, { isLoading }] = usePatchSubmissionAttemptMutation();
  const [value, setValue] = useState(field.value ?? '');
  const [isDismissing, setIsDismissing] = useState(false);

  const handleSave = useCallback(async () => {
    if (!value.trim()) return;
    try {
      await patchAttempt({
        matterId,
        requestId,
        body: {
          action_type: 'set_field_value',
          payload: {
            field_name: field.field_name,
            value: value.trim(),
            ...(field.field_id ? { field_id: field.field_id } : {}),
          },
        },
      }).unwrap();
    } catch {
      toast.error(`Failed to save ${field.field_name}`);
    }
  }, [matterId, requestId, field, value, patchAttempt]);

  const handleDismiss = useCallback(async () => {
    setIsDismissing(true);
    try {
      await patchAttempt({
        matterId,
        requestId,
        body: {
          action_type: 'dismiss_field',
          payload: {
            field_name: field.field_name,
            ...(field.field_id ? { field_id: field.field_id } : {}),
          },
        },
      }).unwrap();
    } catch {
      toast.error(`Failed to dismiss ${field.field_name}`);
    } finally {
      setIsDismissing(false);
    }
  }, [matterId, requestId, field, patchAttempt]);

  return (
    <div className="rounded-lg border-l-[3px] border-l-zinc-300 border border-zinc-200 px-4 py-3 space-y-2">
      <div className="flex items-center gap-1.5">
        <span className="inline-block h-1.5 w-1.5 rounded-full bg-zinc-400 shrink-0" />
        <span className="text-sm font-medium text-zinc-800">
          {field.field_name}
        </span>
      </div>
      <div className="flex gap-2">
        <Input
          placeholder={`Add ${field.field_name.toLowerCase()} if available...`}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="flex-1"
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSave();
          }}
        />
        <Button
          size="sm"
          onClick={handleSave}
          disabled={isLoading || isDismissing || !value.trim()}
        >
          {isLoading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            'Save'
          )}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleDismiss}
          disabled={isLoading || isDismissing}
        >
          {isDismissing ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            'Skip'
          )}
        </Button>
      </div>
      {field.agent_note && (
        <p className="text-xs text-zinc-500 italic">{field.agent_note}</p>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Confirmation field — has a pre-filled value that needs human verification
// ---------------------------------------------------------------------------

const ConfirmationFieldCard = ({
  field,
  matterId,
  requestId,
}: {
  field: SubmissionAttemptFieldItem;
  matterId: string;
  requestId: string;
}) => {
  const [patchAttempt, { isLoading }] = usePatchSubmissionAttemptMutation();
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(field.value ?? '');
  const [isDismissing, setIsDismissing] = useState(false);

  const handleConfirm = useCallback(async () => {
    try {
      await patchAttempt({
        matterId,
        requestId,
        body: {
          action_type: 'confirm_field',
          payload: {
            field_name: field.field_name,
            value: field.value,
            ...(field.field_id ? { field_id: field.field_id } : {}),
          },
        },
      }).unwrap();
    } catch {
      toast.error(`Failed to confirm ${field.field_name}`);
    }
  }, [matterId, requestId, field, patchAttempt]);

  const handleSaveEdit = useCallback(async () => {
    if (!editValue.trim()) return;
    try {
      await patchAttempt({
        matterId,
        requestId,
        body: {
          action_type: 'set_field_value',
          payload: {
            field_name: field.field_name,
            value: editValue.trim(),
            ...(field.field_id ? { field_id: field.field_id } : {}),
          },
        },
      }).unwrap();
      setIsEditing(false);
    } catch {
      toast.error(`Failed to save ${field.field_name}`);
    }
  }, [matterId, requestId, field, editValue, patchAttempt]);

  const handleDismiss = useCallback(async () => {
    setIsDismissing(true);
    try {
      await patchAttempt({
        matterId,
        requestId,
        body: {
          action_type: 'dismiss_field',
          payload: {
            field_name: field.field_name,
            ...(field.field_id ? { field_id: field.field_id } : {}),
          },
        },
      }).unwrap();
      setIsEditing(false);
    } catch {
      toast.error(`Failed to dismiss ${field.field_name}`);
    } finally {
      setIsDismissing(false);
    }
  }, [matterId, requestId, field, patchAttempt]);

  return (
    <div className="rounded-lg border-l-[3px] border-l-blue-400 border border-zinc-200 px-4 py-3 space-y-2">
      <div className="flex items-center gap-1.5">
        <span className="inline-block h-1.5 w-1.5 rounded-full bg-blue-500 shrink-0" />
        <span className="text-sm font-medium text-zinc-800">
          {field.field_name}
        </span>
      </div>

      {isEditing ? (
        <div className="flex gap-2">
          <Input
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            className="flex-1"
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSaveEdit();
              if (e.key === 'Escape') {
                setIsEditing(false);
                setEditValue(field.value ?? '');
              }
            }}
          />
          <Button
            size="sm"
            onClick={handleSaveEdit}
            disabled={isLoading || isDismissing || !editValue.trim()}
          >
            {isLoading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              'Save'
            )}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={isLoading || isDismissing}
            onClick={() => {
              setIsEditing(false);
              setEditValue(field.value ?? '');
            }}
          >
            Cancel
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDismiss}
            disabled={isLoading || isDismissing}
          >
            {isDismissing ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              'Dismiss'
            )}
          </Button>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <Input
            value={field.value ?? ''}
            readOnly
            className="flex-1 bg-zinc-50"
          />
          <Button
            size="sm"
            className="bg-green-600 hover:bg-green-700 text-white"
            onClick={handleConfirm}
            disabled={isLoading || isDismissing}
          >
            {isLoading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              'Confirm'
            )}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsEditing(true)}
            disabled={isLoading || isDismissing}
          >
            Edit
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDismiss}
            disabled={isLoading || isDismissing}
          >
            {isDismissing ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              'Dismiss'
            )}
          </Button>
        </div>
      )}

      {field.agent_note && (
        <p className="text-xs text-zinc-500 italic">{field.agent_note}</p>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Resolved field — compact key-value row
// ---------------------------------------------------------------------------

const ResolvedFieldRow = ({ field }: { field: SubmissionAttemptFieldItem }) => {
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <span className="text-zinc-500 truncate">{field.field_name}</span>
      <span className="font-medium text-zinc-800 truncate text-right max-w-[180px]">
        {field.value ?? '—'}
      </span>
    </div>
  );
};

const NonBlockingFieldRow = ({
  field,
}: {
  field: SubmissionAttemptFieldItem;
}) => {
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <span className="text-zinc-500 truncate">{field.field_name}</span>
      <span className="text-zinc-500 truncate text-right max-w-[180px]">—</span>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main section
// ---------------------------------------------------------------------------

interface FieldsSectionProps {
  fields: SubmissionAttemptFields;
  matterId: string;
  requestId: string;
}

export const FieldsSection = ({
  fields,
  matterId,
  requestId,
}: FieldsSectionProps) => {
  const [expanded, setExpanded] = useState(true);

  const blockingFields = fields.items.filter(
    (f) => f.disposition === 'blocking',
  );
  const confirmFields = fields.items.filter(
    (f) => f.disposition === 'requires_confirmation',
  );
  const optionalMissingFields = fields.items.filter(
    (f) => f.disposition === 'optional_missing',
  );
  const optionalConfirmFields = fields.items.filter(
    (f) => f.disposition === 'optional_confirmation',
  );
  const nonBlockingFields = fields.items.filter(
    (f) => f.disposition === 'non_blocking',
  );
  const resolvedFields = fields.items.filter(
    (f) => f.disposition === 'resolved',
  );

  const hasBlockers = blockingFields.length > 0 || confirmFields.length > 0;
  const allResolved = !hasBlockers;

  // Build the sub-header description
  const subParts: string[] = [];
  if (blockingFields.length > 0) {
    subParts.push(`${blockingFields.length} missing`);
  }
  if (confirmFields.length > 0) {
    subParts.push(`${confirmFields.length} needs confirmation`);
  }
  if (optionalMissingFields.length + optionalConfirmFields.length > 0) {
    subParts.push(
      `${optionalMissingFields.length + optionalConfirmFields.length} optional`,
    );
  }

  const visibleResolved = expanded
    ? resolvedFields
    : resolvedFields.slice(0, 4);
  const hiddenCount = resolvedFields.length - 4;

  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <div className="flex items-baseline gap-2">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
            Fields
          </span>
          {subParts.length > 0 && (
            <span className="text-xs text-zinc-500">
              {subParts.join(' · ')}
            </span>
          )}
        </div>
        <span
          className={cn(
            'text-xs font-medium',
            allResolved ? 'text-green-600' : 'text-zinc-600',
          )}
        >
          {fields.resolved} / {fields.total}
        </span>
      </div>

      <div className="space-y-2">
        {blockingFields.map((field) => (
          <BlockingFieldCard
            key={field.field_id ?? field.field_name}
            field={field}
            matterId={matterId}
            requestId={requestId}
          />
        ))}

        {confirmFields.map((field) => (
          <ConfirmationFieldCard
            key={field.field_id ?? field.field_name}
            field={field}
            matterId={matterId}
            requestId={requestId}
          />
        ))}

        {(optionalMissingFields.length > 0 ||
          optionalConfirmFields.length > 0) && (
          <div className="pt-2 space-y-2">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
              Optional Identity Fields
            </span>
            {optionalConfirmFields.map((field) => (
              <ConfirmationFieldCard
                key={field.field_id ?? field.field_name}
                field={field}
                matterId={matterId}
                requestId={requestId}
              />
            ))}
            {optionalMissingFields.map((field) => (
              <OptionalFieldCard
                key={field.field_id ?? field.field_name}
                field={field}
                matterId={matterId}
                requestId={requestId}
              />
            ))}
          </div>
        )}

        {resolvedFields.length > 0 && (
          <div className="space-y-1 pt-1">
            {visibleResolved.map((field) => (
              <ResolvedFieldRow
                key={field.field_id ?? field.field_name}
                field={field}
              />
            ))}
          </div>
        )}

        {nonBlockingFields.length > 0 && (
          <div className="pt-2 space-y-1">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
              Dismissed Fields / Blockers
            </span>
            {nonBlockingFields.map((field) => (
              <NonBlockingFieldRow
                key={field.field_id ?? field.field_name}
                field={field}
              />
            ))}
          </div>
        )}

        {hiddenCount > 0 && (
          <button
            type="button"
            className="flex items-center gap-1 text-xs font-medium text-zinc-500 hover:text-zinc-700"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? (
              <>
                <ChevronUp className="h-3 w-3" /> Show fewer
              </>
            ) : (
              <>
                <ChevronDown className="h-3 w-3" /> + {hiddenCount} more
                resolved
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};
